/**
 * The AI Studio's data in the page-session cache (studioCache): Mo's welcome figures (GET /overview), the
 * agents with their success score (the rich agent list, shared with the classic overview), the agent
 * definition and its knowledge list. prefetchStudio warms them (hovering "AI Studio" in the sidebar), so
 * the studio opens with its data. Kept light on purpose: the sidebar imports it.
 */
import { useEffect, useMemo } from 'react';
import { invalidate, load, peek, prefetch, useCached } from './studioCache';
import { studioApi } from './studioApi';

export const overviewKey = (botId) => `overview:${botId}`;
export const specKey = (botId) => `spec:${botId}`;
export const docsKey = (botId) => `docs:${botId}`;
export const packagesKey = (botId, lang) => `packages:${botId}:${lang}`;
// the list belongs to the signed-in user
export const richBotsKey = () => `richbots:${(typeof localStorage !== 'undefined' && localStorage.getItem('user_id')) || ''}`;

export async function fetchOverview(botId) {
  const figures = await studioApi.get(botId, '/overview');
  if (!figures || typeof figures.conversations_7d !== 'number') throw new Error('no figures');
  return figures;
}

/** All agents of the user with their success score; on failure the last list (or none). */
export async function fetchRichBots() {
  try {
    const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${
      localStorage.getItem('user_id') || ''
    }&include_knowledge=False&include_success_score=True`;
    const res = await fetch(url);
    if (!res.ok) throw new Error();
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return peek(richBotsKey()) || [];
  }
}

// the sources and how many there are (the backend caps the list only for extreme cases)
export const fetchDocs = (botId) => studioApi.get(botId, '/knowledge')
  .then((r) => ({ items: r.documents || [], total: r.total ?? (r.documents || []).length }));

export const loadRichBots = () => load(richBotsKey(), fetchRichBots).catch(() => null);
export const loadOverview = (botId) => load(overviewKey(botId), () => fetchOverview(botId)).catch(() => null);

/** The agent changed (a skill, a rule, its knowledge, its description): the success score and the figures may
 *  have moved. Outdated at once (a later visit reloads them), reloaded now. */
export function agentChanged(botId) {
  invalidate(overviewKey(botId), richBotsKey());
  return Promise.all([loadOverview(botId), loadRichBots()]);
}

/** A test chat used credits (ub:usage-updated): the numbers change. */
function useUsageRefresh(fn) {
  useEffect(() => {
    window.addEventListener('ub:usage-updated', fn);
    return () => window.removeEventListener('ub:usage-updated', fn);
  }, [fn]);
}

/** Mo's welcome figures for the agent (null until there are any). */
export function useStudioFigures(botId) {
  const figures = useCached(botId ? overviewKey(botId) : null, () => fetchOverview(botId));
  const onUsage = useMemo(() => () => { if (botId) loadOverview(botId); }, [botId]);
  useUsageRefresh(onUsage);
  return figures;
}

/** The rich agent list (null while loading). */
export function useRichBots() {
  const list = useCached(richBotsKey(), fetchRichBots);
  useUsageRefresh(loadRichBots);
  return list;
}

/** One agent from the rich list (with its success score). */
export function useRichBot(botId) {
  const list = useRichBots();
  return useMemo(() => (list || []).find((b) => String(b.id) === String(botId)) || null, [list, botId]);
}

/** Warm what the studio shows first (nothing waits for it). */
export function prefetchStudio(botId) {
  if (!botId) return;
  prefetch(overviewKey(botId), () => fetchOverview(botId));
  prefetch(richBotsKey(), fetchRichBots);
  prefetch(specKey(botId), () => studioApi.get(botId, ''));
  prefetch(docsKey(botId), () => fetchDocs(botId));
}
